// i18n
import './careflow/ph-core/internationalization/locales/i18n';

// highlight
import './utils/highlight';

// scroll bar
import 'simplebar/src/simplebar.css';

// lightbox
import 'react-image-lightbox/style.css';

// // map
// import './utils/mapboxgl';
// import 'mapbox-gl/dist/mapbox-gl.css';

// editor
import 'react-quill/dist/quill.snow.css';

// slick-carousel
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import './assets/css/main.css';

// lazy image
import 'react-lazy-load-image-component/src/effects/black-and-white.css';
import 'react-lazy-load-image-component/src/effects/blur.css';
import 'react-lazy-load-image-component/src/effects/opacity.css';

import '@fullcalendar/react/dist/vdom';
import ReactDOM from 'react-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/lib/integration/react';
// @mui
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
// redux
import { persistor, store } from './redux/store';
// contexts
import { CollapseDrawerProvider } from './contexts/CollapseDrawerContext';
import { SettingsProvider } from './careflow/ph-core/multi-tenant/setting/SettingsContext';

// Check our docs
// https://docs-minimals.vercel.app/authentication/ts-version

// import { AuthProvider } from './contexts/Auth0Context';
// import { AuthProvider } from './contexts/FirebaseContext';
// import { AuthProvider } from './contexts/AwsCognitoContext';

//
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { enablePatches } from 'immer';
import App from './App';
import { REACT_APP_SENTRY_DSN_KEY } from './config';
import reportWebVitals from './reportWebVitals';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { TenantThemeProvider } from './careflow/ph-core/multi-tenant/tenant/context/TenantThemeContext';
import { AuthProvider } from './careflow/ph-core/authentication/context/JWTContext';
import TenantProvider from './careflow/ph-core/multi-tenant/tenant/context/TenantProvider';
import { registerBasicComponents } from './careflow/ph-core/workflow/registry/basicComponents';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
    },
  },
});

enablePatches();
// Set top level domain for sharing cookies across subdomains and allow injecting scripts
const spDomain = document.domain.split('.');

if (spDomain.length > 2) {
  document.domain = spDomain.slice(-2).join('.');
}

// ----------------------------------------------------------------------
if (REACT_APP_SENTRY_DSN_KEY) {
  Sentry.init({
    dsn: REACT_APP_SENTRY_DSN_KEY,

    // Adjust this value in production, or use tracesSampler for greater control
    tracesSampleRate: 1,

    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug: false,

    replaysOnErrorSampleRate: 1.0,

    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.1,

    // You can remove this option if you're not planning to use the Sentry Session Replay feature:
    integrations: [
      new Sentry.Replay({
        maskAllText: true,
        blockAllMedia: true,
      }),
      new Sentry.BrowserTracing(),
    ],
  });
}

registerBasicComponents();

ReactDOM.render(
  <TenantProvider>
    <AuthProvider>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <TenantThemeProvider>
            <ReduxProvider store={store}>
              <PersistGate loading={null} persistor={persistor}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <SettingsProvider>
                    <CollapseDrawerProvider>
                      <App />
                    </CollapseDrawerProvider>
                  </SettingsProvider>
                </LocalizationProvider>
              </PersistGate>
            </ReduxProvider>
          </TenantThemeProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </AuthProvider>
  </TenantProvider>,
  document.getElementById('root')
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
